import type React from "react";
import {type ReactNode, useEffect, useState} from "react";

import {useCurrentUserSettings} from "~/api/hooks/useSettings";

import {type LogEntry, LoggingContext} from "./LoggingContext";

const stringifySafe = (value: unknown): string => {
  if (typeof value !== "object" || value === null) {
    return String(value);
  }

  try {
    const seen = new WeakSet();

    return JSON.stringify(value, (_, v) => {
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) {
          return "[Circular]";
        }
        seen.add(v);
      }

      return v;
    });
  } catch (error) {
    return `[Unserializable Object: ${Object.prototype.toString.call(value)}]`;
  }
};

export const LoggingProvider: React.FC<{children: ReactNode}> = ({
  children,
}) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const {data: settings} = useCurrentUserSettings();
  const enabled = settings?.enableLogViewer ?? false;

  useEffect(() => {
    // Store original console methods
    const originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
    };

    // Only override console methods if logging is enabled
    if (enabled) {
      // Override console methods to capture logs
      console.log = (...args: any[]) => {
        const message = args.map(stringifySafe).join(" ");

        setLogs((prevLogs) => [
          ...prevLogs,
          {type: "log", message, timestamp: new Date()},
        ]);
        originalConsole.log(...args);
      };

      console.info = (...args: any[]) => {
        const message = args.map(stringifySafe).join(" ");

        setLogs((prevLogs) => [
          ...prevLogs,
          {type: "info", message, timestamp: new Date()},
        ]);
        originalConsole.info(...args);
      };

      console.warn = (...args: any[]) => {
        const message = args.map(stringifySafe).join(" ");

        setLogs((prevLogs) => [
          ...prevLogs,
          {type: "warn", message, timestamp: new Date()},
        ]);
        originalConsole.warn(...args);
      };

      console.error = (...args: any[]) => {
        const message = args.map(stringifySafe).join(" ");

        setLogs((prevLogs) => [
          ...prevLogs,
          {type: "error", message, timestamp: new Date()},
        ]);
        originalConsole.error(...args);
      };

      // Add global error handler
      const handleGlobalError = (event: ErrorEvent) => {
        setLogs((prevLogs) => [
          ...prevLogs,
          {
            type: "error",
            message: `Uncaught error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
            timestamp: new Date(),
          },
        ]);
      };

      // Add promise rejection handler
      const handlePromiseRejection = (event: PromiseRejectionEvent) => {
        setLogs((prevLogs) => [
          ...prevLogs,
          {
            type: "error",
            message: `Unhandled promise rejection: ${event.reason}`,
            timestamp: new Date(),
          },
        ]);
      };

      window.addEventListener("error", handleGlobalError);
      window.addEventListener("unhandledrejection", handlePromiseRejection);

      // Restore original console methods on cleanup
      return () => {
        console.log = originalConsole.log;
        console.info = originalConsole.info;
        console.warn = originalConsole.warn;
        console.error = originalConsole.error;
        window.removeEventListener("error", handleGlobalError);
        window.removeEventListener(
          "unhandledrejection",
          handlePromiseRejection,
        );
      };
    }

    // If logging is disabled, just return a no-op cleanup function
    return () => {};
  }, [enabled]);

  const clearLogs = () => {
    setLogs([]);
  };

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  const exportLogs = () => {
    if (logs.length === 0) return;

    // Format logs as text
    const logText = logs
      .map(
        (log) =>
          `[${log.timestamp.toLocaleString()}] [${log.type.toUpperCase()}] ${log.message}`,
      )
      .join("\n");

    // Create a blob and download link
    const blob = new Blob([logText], {type: "text/plain"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logs-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.txt`;
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <LoggingContext.Provider
      value={{
        logs,
        clearLogs,
        isVisible,
        toggleVisibility,
        exportLogs,
        enabled,
      }}
    >
      {children}
    </LoggingContext.Provider>
  );
};
